<script setup lang="ts">
import { isSameDay, formatDate, isBeforeDay } from "~/utils/dateHelpers";

interface DeliveryDay {
  date: string;
  available: boolean;
  closeTime?: string | boolean;
  openTime?: string | boolean;
  [key: string]: unknown;
}

interface CalendarDay {
  date: Date;
  number: string;
  day: string;
  month: string;
  year: string;
  full: string;
  string: string;
  iso: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  inPast: boolean;
  inFuture: boolean;
  available: boolean;
  isScheduled: boolean;
  isReserved: boolean;
  isListItem: boolean;
  closeTime: string | boolean;
  openTime: string | boolean;
  highlightColour: string;
}

interface Props {
  calendarLegend?: Record<string, string>;
  deliveryDays?: DeliveryDay[] | boolean;
  chosenDate?: CalendarDay | boolean;
  dateList?: string[] | boolean;
  scheduledList?: string[] | boolean;
  reservedDays?: string[] | boolean;
  hoveredDate?: string | boolean;
  allowPastDateClick?: boolean;
  disableFutureDate?: boolean;
  width?: string;
  yearly?: boolean;
  fullScreen?: boolean;
  cutOffDay?: number | boolean;
  isPremium?: boolean;
  maker?: Record<string, unknown>;
}

const props = withDefaults(defineProps<Props>(), {
  calendarLegend: () => ({}),
  deliveryDays: false,
  chosenDate: false,
  dateList: false,
  scheduledList: false,
  reservedDays: false,
  hoveredDate: false,
  allowPastDateClick: false,
  disableFutureDate: false,
  width: "100%",
  yearly: false,
  fullScreen: false,
  cutOffDay: false,
  isPremium: false,
  maker: undefined,
});

const emit = defineEmits<{
  returnDate: [date: CalendarDay];
  closeCalendar: [];
  returnHovered: [date: string | false];
}>();

const todayDate = new Date();
const todayStart = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate());

const currentMonth = ref(todayDate.getMonth());
const currentYear = ref(todayDate.getFullYear());

const dayHeaders = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const defaultLegend: Record<string, string> = { Today: "pink-ring" };
const defaultLegendStyles = {
  scheduled: "green",
  reserved: "pink-dash",
  list: "orange",
};

const displayMonth = computed(() => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return monthNames[currentMonth.value];
});

const blanks = computed(() => {
  const firstDay = new Date(currentYear.value, currentMonth.value, 1);
  const startWeekday = firstDay.getDay();
  return startWeekday === 0 ? 6 : startWeekday - 1;
});

const dates = computed<CalendarDay[]>(() => {
  const year = currentYear.value;
  const month = currentMonth.value;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  return Array.from({ length: daysInMonth }, (_, i) => {
    const d = new Date(year, month, i + 1);
    return createDay(d);
  });
});

const mergedLegend = computed(() => ({
  ...defaultLegend,
  ...props.calendarLegend,
}));

const hasLegend = computed(() => Object.keys(props.calendarLegend).length > 0);

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function toISODate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function createDay(date: Date): CalendarDay {
  const fullDate = toISODate(date);
  const dayName = formatDate(date, "dddd");
  const isToday = isSameDay(date, todayDate);
  const inPast = isBeforeDay(date, todayStart);
  const inFuture = !inPast;

  // Check delivery day availability
  let available = true;
  let deliveryObj: DeliveryDay | undefined;

  if (props.deliveryDays && Array.isArray(props.deliveryDays)) {
    deliveryObj = props.deliveryDays.find((day) => day.date === fullDate);
    available = deliveryObj?.available || false;
  }

  const isScheduled = !!(
    props.scheduledList &&
    Array.isArray(props.scheduledList) &&
    props.scheduledList.includes(fullDate)
  );
  const isReserved = !!(
    props.reservedDays &&
    Array.isArray(props.reservedDays) &&
    props.reservedDays.includes(dayName) &&
    date.getTime() > new Date(Date.now() + 48 * 60 * 60 * 1000).getTime()
  );
  const isListItem = !!(
    props.dateList &&
    Array.isArray(props.dateList) &&
    props.dateList.includes(fullDate)
  );

  const highlightColour = isScheduled
    ? defaultLegendStyles.scheduled
    : isReserved
      ? defaultLegendStyles.reserved
      : isListItem
        ? defaultLegendStyles.list
        : isToday
          ? defaultLegend.Today
          : "";

  return {
    date,
    number: String(date.getDate()),
    day: dayName,
    month: formatDate(date, "MMMM"),
    year: formatDate(date, "YYYY"),
    full: fullDate,
    string: `${dayName} ${formatDate(date, "Do")} ${formatDate(date, "MMMM")} ${formatDate(date, "YYYY")}`,
    isCurrentMonth: true,
    isToday,
    inPast,
    inFuture,
    available,
    isScheduled,
    isReserved,
    isListItem,
    closeTime: deliveryObj?.closeTime ?? false,
    openTime: deliveryObj?.openTime ?? false,
    highlightColour,
  };
}

function prevYear() {
  currentYear.value--;
}

function nextYear() {
  currentYear.value++;
}

function prevMonth() {
  if (currentMonth.value === 0) {
    currentMonth.value = 11;
    currentYear.value--;
  } else {
    currentMonth.value--;
  }
}

function nextMonth() {
  if (currentMonth.value === 11) {
    currentMonth.value = 0;
    currentYear.value++;
  } else {
    currentMonth.value++;
  }
}

function chooseDate(allow: boolean, date: CalendarDay) {
  if (allow) {
    emit("returnDate", date);
    emit("closeCalendar");
  }
}

function updateHoveredDate(date: CalendarDay | false) {
  emit("returnHovered", date ? date.full : false);
}

function isDateClickable(date: CalendarDay): boolean {
  return (
    (!date.inPast && date.available) ||
    date.isScheduled ||
    (date.available && date.isToday) ||
    (date.inPast && props.allowPastDateClick)
  );
}

function getDayClasses(date: CalendarDay) {
  const isSelected =
    props.chosenDate &&
    typeof props.chosenDate === "object" &&
    (props.chosenDate as CalendarDay).number === date.number &&
    (props.chosenDate as CalendarDay).month === date.month &&
    (props.chosenDate as CalendarDay).year === date.year;

  const isUnavailable =
    (date.inPast && !props.allowPastDateClick) ||
    (!date.available && !date.isScheduled) ||
    (date.inFuture && props.disableFutureDate);

  const isHovered =
    props.hoveredDate === date.full &&
    props.dateList &&
    Array.isArray(props.dateList) &&
    props.dateList.includes(date.full);

  const hasHighlight =
    (date.inPast && props.allowPastDateClick) ||
    (date.available && date.isScheduled) ||
    (date.inFuture && !props.disableFutureDate);

  return {
    "date-selected": isSelected,
    today: date.isToday,
    unavailable: isUnavailable,
    "past-dates": date.inPast && !props.allowPastDateClick,
    hovered: isHovered,
    [`day-highlight-colour-${date.highlightColour}`]: hasHighlight && date.highlightColour,
  };
}
</script>

<template>
  <div
    class="calendar"
    :class="{ 'full-screen': fullScreen }"
    :style="`width: ${width}`"
  >
    <div
      v-if="yearly"
      class="calendar__head"
    >
      <fa
        class="calendar__head--arrow icon"
        :icon="['fal', 'chevron-left']"
        @click="prevYear"
      />
      <h4>{{ currentYear }}</h4>
      <fa
        class="calendar__head--arrow icon"
        :icon="['fal', 'chevron-right']"
        @click="nextYear"
      />
    </div>
    <div class="calendar__head">
      <fa
        class="calendar__head--arrow icon"
        :icon="['fal', 'chevron-left']"
        @click="prevMonth"
      />
      <h4 class="grey-text small-text">{{ displayMonth }}</h4>
      <fa
        class="calendar__head--arrow icon"
        :icon="['fal', 'chevron-right']"
        @click="nextMonth"
      />
    </div>
    <div class="calendar__days">
      <h4
        v-for="(day, i) in dayHeaders"
        :key="`day_${i}`"
        class="calendar__days--title small-text grey-text"
      >
        {{ day }}
      </h4>
    </div>
    <div class="calendar__days">
      <div
        v-for="(_, i) in blanks"
        :key="`blank_${i}`"
        class="calendar__days--day"
      ></div>
      <div
        v-for="(date, i) in dates"
        :key="`date_${i}`"
        class="calendar__days--day"
        @mouseover="dateList && updateHoveredDate(date)"
        @mouseout="dateList && updateHoveredDate(false)"
        @click="chooseDate(isDateClickable(date), date)"
      >
        <p
          class="grey-text small-text"
          :class="getDayClasses(date)"
        >
          {{ date.number }}
        </p>
      </div>
    </div>
    <div
      v-if="hasLegend"
      class="calendar__legend"
    >
      <div
        v-for="(colour, label) in mergedLegend"
        :key="String(label)"
      >
        <div class="calendar__legend-item">
          <div
            class="calendar__legend-item-dot"
            :class="`day-highlight-colour-${colour}`"
          ></div>
          <p class="calendar__legend-item-text grey-text small-text">
            {{ label }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.calendar {
  padding: 0.5rem 1% 1rem;
  transition: 0.5s;
  background-color: var(--color-white-1);
  border-radius: 0.4rem;
  z-index: 6666;
  overflow-x: hidden;
  overflow-y: auto;
  border: 2px solid var(--color-grey-3);
  max-width: 28rem;

  display: flex;
  flex-direction: column;

  @media (max-width: $laptop) {
    width: 98%;
    left: 0;
    top: 70px;
  }

  &__head,
  &__dates,
  &__days {
    display: flex;
  }

  &__dates,
  &__days {
    flex-wrap: wrap;
    align-items: center;
    text-align: center;
    &--day,
    &--title {
      width: calc(100% / 7);
      cursor: pointer;
    }
  }

  &__head {
    justify-content: space-around;
    align-items: center;
    padding: 0.3125rem 0;
    // Calendar nav chevrons → muted-foreground. Pink stays only on the
    // active-date circle (further down), never on the chrome.
    &--arrow {
      cursor: pointer;
      color: var(--color-grey-2);
      transition: color 0.15s ease, transform 0.15s ease;
      &:hover {
        color: var(--color-pink-1) !important;
        transform: scale(1.2);
      }
    }
  }

  &__dates {
    h4 {
      padding: 5px 0;
      color: var(--color-grey-1);
    }
  }

  &__days {
    &--day {
      padding: 2px 0;
      color: var(--color-pink-3);
      margin: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 2.6rem;
      p {
        height: 1.75rem;
        width: 1.75rem;
        box-sizing: border-box;
        display: flex;
        cursor: pointer;
        border: 2px solid transparent;
        border-radius: 50%;
        align-items: center;
        justify-content: center;
        margin: 0.2rem 0;
        transition:
          background-color 0.15s ease,
          border-color 0.15s ease;
        &:not(.date-selected):not(.today):not(.unavailable):not(.past-dates):hover {
          background-color: var(--color-pink-4);
          border-color: var(--color-pink-4);
        }
      }
    }
    .unavailable {
      cursor: default !important;
      // Match the visual treatment of past dates — `.past-dates` already
      // dims with opacity 0.6, so a closed maker day in the future should
      // read the same way. Without this, a future closed day only differs
      // by text colour, which customers consistently miss and end up
      // trying to click through. Today is excluded so the pink "today"
      // marker stays full opacity.
      &:not(.today) {
        color: var(--color-grey-2) !important;
        opacity: 0.6;
      }
    }
    .date-selected {
      background-color: var(--color-white-1);
      border-color: var(--color-pink-1) !important;
      box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.15);
    }
    .today {
      background-color: var(--color-pink-1);
      color: var(--color-white-1) !important;
    }
    .past-dates,
    .future-dates {
      color: var(--color-grey-2);
      cursor: default !important;
      opacity: 0.6;
    }
    .hovered {
      border: 2px solid var(--color-pink-1);
    }
  }

  &__legend {
    align-self: center;
    display: flex;
    grid-template-columns: repeat(auto-fit, minmax(50%, 1fr));
    gap: 0;
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1.5px solid var(--color-grey-3);
    width: 90%;
    justify-content: space-between;
    @media (max-width: $laptop) {
      display: grid;
    }
    &-item {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      &-dot {
        height: 1rem;
        width: 1rem;
        display: flex;
        cursor: pointer;
        border-radius: 50%;
        color: var(--color-primary) !important;
        align-items: center;
        justify-content: center;
        margin: 0 0.2rem 0 0;
      }
    }
  }
}

.day-highlight-colour {
  &-grey {
    color: var(--color-grey-1) !important;
    background-color: var(--color-grey-3) !important;
    border: 2px solid var(--color-grey-3) !important;
  }
  &-pink {
    color: var(--color-white-1) !important;
    background-color: var(--color-pink-1) !important;
    border: 2px solid var(--color-pink-1) !important;
  }
  &-pink-ring {
    border: 2px solid var(--color-pink-1) !important;
  }
  &-pink-dash {
    border: 2px dotted var(--color-pink-1);
  }
  &-orange {
    color: var(--color-white-1) !important;
    background-color: var(--color-orange-1) !important;
    border: 2px solid var(--color-orange-1) !important;
  }
  &-green {
    color: var(--color-white-1) !important;
    background-color: var(--color-green-2) !important;
    border: 2px solid var(--color-green-2) !important;
  }
}

.full-screen {
  position: absolute;
  width: 100%;
  top: 0;
}
</style>
